import { useEffect, useMemo, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { sortBy, isShallowEqual, nonNullable } from 'lib/array/arrayUtils'
import { formatDynamicDateRange } from 'lib/datetime/dateUtils'
import type {
  CartableFlightSavedItemData,
  LeFlightSavedItemData,
  TransportOption,
  TripItineraryData,
} from 'tripPlanner/api/types'
import {
  getCartPanelExperienceCartTime,
  getMultiCartExperienceCheckoutItem,
  isCartPanelExperienceUnavailableStatus,
  isCartPanelItemCheckoutDataLoading,
  isCartPanelLinkedCheckoutProjectionPending,
  isMultiCartItemCheckoutActive,
  isMultiCartItemPurchased,
  resolveCartPanelExperienceDisplayStatus,
  toCartPanelExperienceTickets,
  type CartPanelExperienceTicket,
  type CartPanelItemCartStatus,
} from '../utils/cartPanelUtils'
import {
  extractFlightCartPanelOptions,
  toFlightCartPanelProjectionSignature,
  type ExtractedFlightCartPanelOption,
} from '../utils/flightCartPanelUtils'
import {
  extractTripChatCartCandidates,
  toTripChatCartCandidateSignature,
  type TripChatAccommodationCartCandidate,
  type TripChatExperienceCartCandidate,
} from '../utils/tripChatCartCandidateUtils'
import {
  toTripChatAccommodationPricingInput,
  toTripChatExperiencePricingInput,
} from '../utils/tripChatCartPricingInputUtils'
import { getTripPlannerCandidateCartItem } from '../utils/tripPlannerCartItemsUtils'
import type { ResolvedItemPricing, PricingInput } from './useItineraryItemPricing'
import { useItineraryItemPricing } from './useItineraryItemPricing'
import { useExperienceCartItemPricing, type ExperiencePricingInput } from './useExperienceCartItemPricing'
import { useFlightCartExpiryTick } from './useFlightCartExpiryTick'
import {
  tripPlannerCartItemsKey,
  useTripPlannerCartItems,
} from './useTripPlannerCartItems'

export interface CartPanelItemPricing {
  price: number | undefined
  memberPrice: number | undefined
  value: number | undefined
  isAvailable: boolean | undefined
  loading: boolean
  error: boolean
}

interface CartPanelBaseItem {
  key: string
  source: 'cart' | 'itinerary'
  cartStatus: CartPanelItemCartStatus
  cartExcluded: boolean
  isBooked: boolean
  name: string
  imageId: string | undefined
  pricing: CartPanelItemPricing
  includeInTotal: boolean
  cartItemId: string | undefined
  tripItemId: number
  cartItem: App.MultiCartItem | undefined
}

export interface CartPanelAccommodationItem extends CartPanelBaseItem {
  productType: 'accommodation'
  roomImageId: string | undefined
  checkIn: string | undefined
  checkOut: string | undefined
  dates: string | undefined
  duration: number | undefined
  packageId: string | undefined
  roomRateId: string | undefined
  roomName: string | undefined
  occupancy: string | undefined
  packageAutoSelected: boolean | undefined
  availableDurations: Array<number> | undefined
  offerId: string | undefined
}

export interface CartPanelExperienceItem extends CartPanelBaseItem {
  productType: 'experience'
  experienceId: string
  date: string | undefined
  itineraryDate: string | undefined
  time: string | undefined
  tickets: Array<CartPanelExperienceTicket>
  locationName: string | undefined
  bookingType: string | undefined
  isBuyNowBookLater: boolean
}

export interface CartPanelFlightItem extends CartPanelBaseItem {
  productType: 'flight'
  pairedTripItemIds: Array<number>
  returnTripItemId: number | undefined
  option: TransportOption
  returnOption: TransportOption | undefined
  savedItemData: CartableFlightSavedItemData
  returnSavedItemData: LeFlightSavedItemData | undefined
  flightCartItem: App.MultiCartFlightItem | undefined
  isAvailabilityBlocked: boolean
  hasRefreshFailed: boolean
}

export type CartPanelItem = CartPanelAccommodationItem | CartPanelExperienceItem | CartPanelFlightItem

interface UseCartPanelItemsArgs {
  multiCartId: string | undefined
  itineraryData: TripItineraryData | undefined
  isLoggedIn: boolean
  optimisticExclusions: Record<number, boolean>
  refreshFailedFlightKeys: Record<string, boolean>
}

interface UseCartPanelItemsReturn {
  items: Array<CartPanelItem>
  isLoading: boolean
  isError: boolean
  refetch: () => void
  refetchCartItems: () => Promise<Map<string, App.MultiCartItem>>
}

type ExtractedAccommodationItem = TripChatAccommodationCartCandidate
type ExtractedExperienceItem = TripChatExperienceCartCandidate

interface CartPanelFlightRefreshKeyInput {
  tripItemId: number
  cartItemId: string | undefined
}

export function getCartPanelFlightRefreshKey(item: CartPanelFlightRefreshKeyInput): string {
  return item.cartItemId ?? `${item.tripItemId}`
}

function getCartItemSource(cartItem: App.MultiCartItem | undefined): 'cart' | 'itinerary' {
  return cartItem ? 'cart' : 'itinerary'
}

function hasLoadingIncludedPrice(item: CartPanelItem): boolean {
  return item.includeInTotal && isCartPanelItemCheckoutDataLoading(item)
}

function mapAccommodationItem(
  item: ExtractedAccommodationItem,
  pricing: ResolvedItemPricing,
  cartItem: App.MultiCartItem | undefined,
  cartExcluded: boolean,
): CartPanelAccommodationItem {
  const includeInTotal = (
    item.cartStatus === 'active' &&
    pricing.isAvailable !== false &&
    isMultiCartItemCheckoutActive(cartItem)
  )

  return {
    key: `${item.tripItemId}`,
    source: getCartItemSource(cartItem),
    cartStatus: item.cartStatus,
    cartExcluded,
    productType: 'accommodation',
    isBooked: item.isBooked,
    name: item.name,
    imageId: item.imageId,
    roomImageId: item.roomImageId,
    checkIn: item.checkIn,
    checkOut: item.checkOut,
    dates: item.checkIn && item.checkOut
      ? formatDynamicDateRange(item.checkIn, item.checkOut)
      : undefined,
    duration: item.duration,
    packageId: item.packageId,
    roomRateId: item.roomRateId,
    roomName: item.roomName,
    occupancy: item.occupancy,
    packageAutoSelected: item.packageAutoSelected,
    availableDurations: item.availableDurations,
    pricing,
    includeInTotal,
    cartItemId: item.cartItemId,
    tripItemId: item.tripItemId,
    offerId: item.offerId,
    cartItem,
  }
}

function mapExperienceItem(
  item: ExtractedExperienceItem,
  pricing: CartPanelItemPricing,
  cartItem: App.MultiCartItem | undefined,
  cartExcluded: boolean,
): CartPanelExperienceItem {
  const experienceCartItem = cartItem?.type === 'experience' ? cartItem : undefined
  const experienceCheckoutItem = getMultiCartExperienceCheckoutItem(experienceCartItem)
  const tickets = experienceCheckoutItem ? toCartPanelExperienceTickets(experienceCheckoutItem.tickets) : item.tickets
  const cartStatus = resolveCartPanelExperienceDisplayStatus({
    cartStatus: item.cartStatus,
    bookingType: item.bookingType,
    isBuyNowBookLater: item.isBuyNowBookLater,
    isGift: experienceCartItem?.isGift ?? false,
    cartItem: experienceCartItem,
    hasSelectedTickets: tickets.length > 0,
    isAvailable: pricing.isAvailable,
  })
  const date = experienceCartItem?.date ?? item.date
  const time = experienceCartItem ? getCartPanelExperienceCartTime(experienceCartItem) : item.time
  const isBuyNowBookLater = experienceCartItem?.isBuyNowBookLater ?? item.isBuyNowBookLater
  const includeInTotal = !isCartPanelExperienceUnavailableStatus(cartStatus)

  return {
    key: `${item.tripItemId}`,
    source: getCartItemSource(cartItem),
    cartStatus,
    cartExcluded,
    productType: 'experience',
    isBooked: item.isBooked,
    name: item.name,
    imageId: item.imageId,
    pricing,
    includeInTotal,
    cartItemId: item.cartItemId,
    tripItemId: item.tripItemId,
    cartItem,
    experienceId: item.experienceId,
    date,
    itineraryDate: item.itineraryDate,
    time,
    tickets,
    locationName: item.locationName,
    bookingType: item.bookingType,
    isBuyNowBookLater,
  }
}

function getOptimisticFlightExclusion(
  item: ExtractedFlightCartPanelOption,
  optimisticExclusions: Record<number, boolean>,
): boolean {
  for (const tripItemId of item.pairedTripItemIds) {
    const optimisticValue = optimisticExclusions[tripItemId]
    if (optimisticValue !== undefined) return optimisticValue
  }

  return item.cartExcluded
}

function mapFlightItem(
  item: ExtractedFlightCartPanelOption,
  cartExcluded: boolean,
  hasRefreshFailed: boolean,
): CartPanelFlightItem {
  const price = item.cartStatus === 'active' && item.cartPrice !== undefined
    ? item.cartPrice
    : item.savedPrice

  return {
    key: `${item.tripItemId}`,
    source: getCartItemSource(item.cartItem),
    cartStatus: item.cartStatus,
    cartExcluded,
    productType: 'flight',
    isBooked: false,
    name: item.option.label ?? 'Flight',
    imageId: item.savedItemData.carrierLogo,
    pricing: {
      price,
      memberPrice: undefined,
      value: undefined,
      isAvailable: !item.isAvailabilityBlocked,
      loading: false,
      error: false,
    },
    includeInTotal: item.includeInTotal,
    cartItemId: item.cartItemId,
    tripItemId: item.tripItemId,
    cartItem: item.cartItem,
    pairedTripItemIds: item.pairedTripItemIds,
    returnTripItemId: item.returnTripItemId,
    option: item.option,
    returnOption: item.returnOption,
    savedItemData: item.savedItemData,
    returnSavedItemData: item.returnSavedItemData,
    flightCartItem: item.cartItem,
    isAvailabilityBlocked: item.isAvailabilityBlocked,
    hasRefreshFailed,
  }
}

export function useCartPanelItems({
  multiCartId,
  itineraryData,
  isLoggedIn,
  optimisticExclusions,
  refreshFailedFlightKeys,
}: UseCartPanelItemsArgs): UseCartPanelItemsReturn {
  const queryClient = useQueryClient()

  const itineraryItems = useMemo(
    () => extractTripChatCartCandidates(itineraryData),
    [itineraryData],
  )

  const accommodationItems = useMemo(
    () => itineraryItems.filter((item): item is ExtractedAccommodationItem => item.productType === 'accommodation'),
    [itineraryItems],
  )

  const experienceItems = useMemo(
    () => itineraryItems.filter((item): item is ExtractedExperienceItem => item.productType === 'experience'),
    [itineraryItems],
  )

  const {
    cartItemsMap,
    isLoading: cartLoading,
    isError: cartError,
    refetch,
    refetchCartItems,
  } = useTripPlannerCartItems({ multiCartId, isLoggedIn })
  const flightCartNow = useFlightCartExpiryTick(cartItemsMap)

  const pricingInputs = useMemo<Array<PricingInput>>(
    () => accommodationItems.map(item => toTripChatAccommodationPricingInput(item, getTripPlannerCandidateCartItem(cartItemsMap, item))),
    [accommodationItems, cartItemsMap],
  )

  const experiencePricingInputs = useMemo<Array<ExperiencePricingInput>>(
    () => experienceItems.map(item => toTripChatExperiencePricingInput(item, getTripPlannerCandidateCartItem(cartItemsMap, item))),
    [experienceItems, cartItemsMap],
  )

  const pricing = useItineraryItemPricing(pricingInputs)
  const experiencePricing = useExperienceCartItemPricing(experiencePricingInputs, isLoggedIn)

  const flightItems = useMemo<Array<ExtractedFlightCartPanelOption>>(() => {
    if (!itineraryData?.stops) return []
    return itineraryData.stops.flatMap(stop => extractFlightCartPanelOptions(stop.transports, cartItemsMap, flightCartNow))
  }, [itineraryData, cartItemsMap, flightCartNow])

  const syncedCartItemSignatures = useMemo(
    () => nonNullable([
      ...itineraryItems.map(toTripChatCartCandidateSignature),
      ...flightItems.map(toFlightCartPanelProjectionSignature),
    ]),
    [itineraryItems, flightItems],
  )

  const prevCartItemSignaturesRef = useRef<Array<string>>([])
  useEffect(() => {
    const currentSignatures = sortBy(syncedCartItemSignatures, signature => signature, 'asc')
    if (prevCartItemSignaturesRef.current.length > 0 && !isShallowEqual(prevCartItemSignaturesRef.current, currentSignatures)) {
      if (isLoggedIn && multiCartId) {
        queryClient.invalidateQueries({ queryKey: tripPlannerCartItemsKey(multiCartId) }).catch(() => undefined)
      }
    }
    prevCartItemSignaturesRef.current = currentSignatures
  }, [syncedCartItemSignatures, multiCartId, isLoggedIn, queryClient])

  const items = useMemo<Array<CartPanelItem>>(() => {
    const accommodationAndExperienceItems = nonNullable(
      itineraryItems.map((item): CartPanelItem | undefined => {
        const cartItem = getTripPlannerCandidateCartItem(cartItemsMap, item)
        const effectiveExcluded = optimisticExclusions[item.tripItemId] ?? item.cartExcluded

        if (item.productType === 'accommodation') {
          const resolvedPricing = pricing.get(item.tripItemId)
          if (!resolvedPricing) return undefined
          return mapAccommodationItem(item, resolvedPricing, cartItem, effectiveExcluded)
        }

        const resolvedPricing = experiencePricing.get(item.tripItemId)
        if (!resolvedPricing) return undefined
        return mapExperienceItem(item, resolvedPricing, cartItem, effectiveExcluded)
      }),
    )

    const mappedFlightItems = flightItems.map(item => {
      const refreshKey = getCartPanelFlightRefreshKey(item)
      return mapFlightItem(
        item,
        getOptimisticFlightExclusion(item, optimisticExclusions),
        refreshFailedFlightKeys[refreshKey] ?? false,
      )
    })

    return [...accommodationAndExperienceItems, ...mappedFlightItems].filter(item => !isMultiCartItemPurchased(item.cartItem))
  }, [itineraryItems, pricing, experiencePricing, cartItemsMap, optimisticExclusions, flightItems, refreshFailedFlightKeys])

  const isWaitingForCheckoutProjection = cartLoading && (
    items.length === 0 ||
    items.some(isCartPanelLinkedCheckoutProjectionPending)
  )
  const isLoading = isWaitingForCheckoutProjection || items.some(hasLoadingIncludedPrice)

  return {
    items,
    isLoading,
    isError: cartError,
    refetch,
    refetchCartItems,
  }
}
